// Copyright (c) 2022 by Move Closer

import { AnyObject } from '@movecloser/front-core'
import { AsyncComponent, VueConstructor } from 'vue'
import {
  Container as AbstractContainer,
  ElementIdentifier,
  Module as AbstractModule,
  ModuleSize
} from '@movecloser/page-builder/lib/contracts'

import { IRelatedService } from '../../shared'
import { ContentType } from './content'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface Alignment<HA extends string = string, VA extends string = string> {
  x: { desktop?: HA; mobile?: HA }
  y: { desktop?: VA; mobile?: VA }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type AnyContainer = Container

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type AnyModule<D extends ModuleDriver = string> = Module<ModuleContent, D, string>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface CanBeAlignedHorizontally<A extends string = string> {
  align?: A
  alignDesktop?: A
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface CanBeAlignedInGrid<HA extends string = string, VA extends string = string> {
  alignment?: Partial<Alignment<HA, VA>>
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface CanBeAlignedVertically<A extends string = string> {
  align?: A
  alignDesktop?: A
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface CanBeSnippet {
  source?: SnippetData
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export type CanHaveColor<C extends string> = Partial<HasColor<C>>

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface CanHaveCustomClass {
  customClass?: Array<string>
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export type CanHaveSource<S extends ModuleSource> = Partial<HasSource<S>>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type CanHaveType = Partial<HasType>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type CanHaveVersion<V extends ModuleVersion> = Partial<HasVersion<V>>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type Container =
  (AbstractContainer) &
  CanBeSnippet &
  CanHaveType &
  AnyObject

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface HasColor<C extends string> {
  color: C
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface HasContent<C extends ModuleContent> {
  content: C
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface HasDriver<D extends string = string> {
  driver: D
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export interface HasSource<S extends ModuleSource> {
  source: S
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface HasVersion<V extends ModuleVersion> {
  version: V
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface HasTitle {
  title: ModuleTitle
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface HasType {
  type: string
}

/**
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export interface HasVisibility {
  isVisible: boolean
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type LayoutSlot = string

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 * @author Olga Milczek <olga.milczek@movecloser.pl> (edited)
 */
export type Module<
  C extends ModuleContent,
  D extends ModuleDriver,
  V extends ModuleVersion = ModuleVersion,
  S extends ModuleSource = ModuleSource
  > = (
  AbstractModule &
  CanBeAlignedInGrid &
  CanHaveSource<S> &
  CanHaveVersion<V> &
  HasContent<C> &
  HasDriver<D> &
  HasTitle &
  HasVisibility
  )

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleBase<M extends AnyModule> = Pick<M, 'alignment' | 'content' | 'driver' | 'version'>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleConstructor = AsyncComponent | VueConstructor

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleContent = AnyObject

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleContentResolver<Input extends ModuleContent, Output extends ModuleContent> =
  (input: Input, relatedService: IRelatedService) => Promise<Output>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ModuleFactory<M extends AnyModule = AnyModule> {
  createInitialSize (): ModuleSize
  createModule (): ModuleBase<M>
  getResizeStep (module: M, axis: 'x' | 'y'): number
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleDriver = string

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export enum ModuleMode {
  Default = 'default',
  Dynamic = 'dynamic',
  Smart = 'smart'
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export type ModuleSourceOptions = AnyObject

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export interface ModuleSource<S extends ModuleSourceOptions = AnyObject> {
  type: ModuleMode
  options?: S
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleTitle = string

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleVersion = string

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export type ModuleWithVersion<
  C extends ModuleContent,
  D extends ModuleDriver,
  V extends ModuleVersion,
  > = Module<C, D, V> & HasVersion<V>

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ModulesFormRegistryEntry {
  component: ModuleConstructor
  factory: ModuleFactory
  icon: string
  label: string
  priority?: number
  restrictions?: Partial<ModuleFormRestrictions>
  sourceComponent?: ModuleConstructor
  versions?: Record<string, string>
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ModuleFormRestrictions {
  allowedTypes: ContentType[]
  forbiddenTypes: ContentType[]
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ModulesUiRegistryEntry {
  component: ModuleConstructor
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver: ModuleContentResolver<any, any>
}

export interface SnippetData extends AnyObject {
  id: ElementIdentifier
  label: string
}
